import React from 'react';
import { toast } from 'react-toastify';

import bbApi, { customRequestApp as bbApiExternal } from './service';
import { normalizeBidIncrements, normalizeSuccessFailedBids } from './adapters';

import { LAST_REFRESHED_TIME } from '../../components/BulkBidding/utils';
import { Banner } from '../../components/BulkBidding/Banner';

import {
  FETCH_BID_HISTORY_SUCCESS,
  FETCH_CATALOG_LOTS_PENDING,
  FETCH_PLACED_LOTS_DATA,
  SUBMIT_PLACED_BIDS,
  FETCH_BID_HISTORY,
  THROW_API_ERROR,
  FETCH_SALE_INFO,
  FETCH_CATALOG_LOTS_SUCCESS,
  clearCatalogLots
} from './bulkBidding';
import { SUCCESS_MESSAGES, FAILURE_MESSAGES } from './constants';

// TODO: Refactor to dry headers (user and house ref) config for apis in all thunks

const INITIAL_PAGE_SIZE = 100;

function setUserRefHeader(userRef) {
  bbApi.defaults.headers.common.liveUserRef = userRef;
}

function getBidsBannerMessage(bids) {
  return bids.map((lot, index) => `Lot ${lot}${bids.length !== index + 1 ? ', ' : ''}`);
}

const fetchCatalogLotsFromApi = async (dispatch, catalogRef, pageSize, pageNumber) => {
  const { data } = await bbApi.get(`auctions/bidAdmin/catalog/${catalogRef}/lots`, {
    params: { size: pageSize, page: pageNumber }
  });
  // eslint-disable-next-line no-underscore-dangle
  let { bidAdminLotDataList } = data._embedded;

  bidAdminLotDataList = normalizeBidIncrements(bidAdminLotDataList);

  localStorage.setItem(LAST_REFRESHED_TIME, Date.now().toString());

  dispatch({
    type: FETCH_CATALOG_LOTS_SUCCESS,
    payload: { data: bidAdminLotDataList, pageNumber }
  });
};

export const fetchCatalogLots = (catalogRef, lotCount) => (dispatch, getStore) => {
  try {
    const { user } = getStore();
    if (!user.userRef) throw new Error('userRef value not found!');
    setUserRefHeader(user.userRef);

    const pageSize = lotCount < INITIAL_PAGE_SIZE ? lotCount : INITIAL_PAGE_SIZE;
    const totalPages = Math.ceil(lotCount / pageSize);

    dispatch({ type: FETCH_CATALOG_LOTS_PENDING, payload: totalPages });

    for (let pageNumber = 0; pageNumber < totalPages; pageNumber++) {
      // eslint-disable-next-line no-await-in-loop
      fetchCatalogLotsFromApi(dispatch, catalogRef, pageSize, pageNumber);
    }
  } catch (err) {
    console.error(err.error);
    toast(<Banner bannerTitle={FAILURE_MESSAGES.CATALOG_DATA_NOT_FOUND} isSuccess={false} />);
    dispatch({
      type: THROW_API_ERROR,
      payload: FAILURE_MESSAGES.CATALOG_DATA_NOT_FOUND
    });
  }
};

export const fetchSaleInfo = catalogRef => async (dispatch, getStore) => {
  try {
    const { user } = getStore();
    if (!user.userRef) throw new Error('userRef value not found!');
    setUserRefHeader(user.userRef);
    const { data } = await bbApi.get(`auctions/bidAdmin/catalog/${catalogRef}`);

    dispatch({
      type: FETCH_SALE_INFO,
      payload: data
    });
  } catch (err) {
    console.error(err.error);
    toast(<Banner bannerTitle={FAILURE_MESSAGES.SALE_INFO_NOT_FOUND} isSuccess={false} />);
    dispatch({
      type: THROW_API_ERROR,
      payload: FAILURE_MESSAGES.SALE_INFO_NOT_FOUND,
    });
  }
};

export const fetchLotBidHistory = (lotRef, catalogRef) => async (dispatch, getStore) => {
  try {
    const { user } = getStore();
    if (!user.userRef) throw new Error('userRef value not found!');
    setUserRefHeader(user.userRef);
    dispatch({ type: FETCH_BID_HISTORY });

    const [{ data }, { data: cancelBidCount }] = await Promise.all([
      bbApi.post(`auctions/bidAdmin/catalog/${catalogRef}/bidHistory`, [lotRef]),
      bbApi.get(`auctions/bidAdmin/catalog/${catalogRef}/cancelBidCount`)
    ]);
    dispatch({
      type: FETCH_BID_HISTORY_SUCCESS,
      payload: { bidHistory: data[lotRef], cancelBidCount }
    });
  } catch (ex) {
    console.error(ex.error);
    toast(<Banner bannerTitle={FAILURE_MESSAGES.BID_HISTORY_AND_CANCEL_COUNT_FETCH_ERRROR} isSuccess={false} />);
    dispatch({
      type: THROW_API_ERROR,
      payload: FAILURE_MESSAGES.BID_HISTORY_AND_CANCEL_COUNT_FETCH_ERRROR,
    });
  }
};

export const fetchSelectedLotsData = (catalogRef, lots) => async (dispatch, getStore) => {
  try {
    const { user } = getStore();
    if (!user.userRef) throw new Error('userRef value not found!');
    setUserRefHeader(user.userRef);
    const { data } = await bbApi.post(`auctions/bidAdmin/catalog/${catalogRef}/lotsByRef`, lots);

    dispatch({
      type: FETCH_PLACED_LOTS_DATA,
      payload: normalizeBidIncrements(data)
    });
  } catch (e) {
    console.error(e.error);
    toast(<Banner bannerTitle={FAILURE_MESSAGES.SHOW_SELECTED_LOT_ERROR} isSuccess={false} />);
    dispatch({
      type: THROW_API_ERROR,
      payload: FAILURE_MESSAGES.SHOW_SELECTED_LOT_ERROR,
    });
  }
};

export const submitSelectedBids = (requestData, placedLotsData) => async (dispatch, getStore) => {
  try {
    const { house, user, bulkBidding } = getStore();
    const { lotCount } = bulkBidding.saleInfo;
    if (!user.userRef) throw new Error('userRef value not found!');
    if (!house.houseUserName) throw new Error('houseUserName value not found!');

    setUserRefHeader(user.userRef);

    requestData.liveUserRef = user.userRef;
    requestData.houseRef = house.houseRef;
    const { data: { data } } = await bbApiExternal.post('app/external/orderBid/bidAdmin', requestData);

    dispatch(fetchCatalogLots(requestData.catalogRef, lotCount));
    const bulkBidResponse = data.bulkOrderBidResponse.bidAdminOrderBidResponses;

    const { successBids, failedBids } = normalizeSuccessFailedBids(bulkBidResponse, placedLotsData);

    if (successBids.length > 0) {
      const message = getBidsBannerMessage(successBids);

      toast(<Banner bannerTitle={SUCCESS_MESSAGES.PLACE_BIDS_SUCCESSFUL} message={message} />);
    } else if (failedBids.length > 0) {
      const message = getBidsBannerMessage(failedBids);

      toast(<Banner isSuccess={false} bannerTitle={FAILURE_MESSAGES.PLACE_BIDS_ERROR} message={message} />);
    }

    dispatch({
      type: SUBMIT_PLACED_BIDS,
      payload: { successBids, failedBids },
    });
  } catch (e) {
    console.error(e);
    toast(<Banner bannerTitle={FAILURE_MESSAGES.PLACE_BIDS_ERROR_TRY_AGAIN} isSuccess={false} />);
    dispatch({
      type: THROW_API_ERROR,
      payload: FAILURE_MESSAGES.PLACE_BIDS_ERROR_TRY_AGAIN,
    });
  }
};

export const submitCancelBid = (requestData) => async (dispatch, getStore) => {
  try {
    const { user, house, bulkBidding: { saleInfo: { lotCount } } } = getStore();
    if (!user.userRef) throw new Error('userRef value not found!');
    if (!house.houseUserName) throw new Error('houseUserName value not found!');

    setUserRefHeader(user.userRef);

    requestData.liveUserRef = user.userRef;
    requestData.houseRef = house.houseRef;

    await bbApiExternal.post('app/external/cancelBid/bidAdmin', { ...requestData });

    dispatch(clearCatalogLots());
    dispatch(fetchCatalogLots(requestData.catalogRef, lotCount));

    toast(<Banner bannerTitle={SUCCESS_MESSAGES.BIDS_CANCELED_SUCCESSFULLY} />);
  } catch (e) {
    console.error(e.error);
    toast(<Banner isSuccess={false} bannerTitle={FAILURE_MESSAGES.CANCEL_BIDS_ERROR_TRY_AGAIN} />);
  }
};
